import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { visitDateFromYmdKey } from '../../../../utils/visitDateUtils';
// Removed FontAwesome icons - using simple text/HTML elements instead

const DateContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 0;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: clamp(1.5rem, 4vw, 2rem);
`;

const SectionEyebrow = styled.span`
  display: block;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
  margin-bottom: 0.45rem;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.35rem, 3vw, 1.65rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;
  font-weight: 700;
`;

const SectionSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.75vw, 0.95rem);
  color: rgba(16, 29, 64, 0.55);
  margin: 0 auto;
  max-width: 36rem;
  line-height: 1.55;
`;

const PinnedDateNotice = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  background: rgba(16, 29, 64, 0.06);
  border: 1px solid rgba(16, 29, 64, 0.1);
  border-radius: 10px;
  padding: 0.85rem 1rem;
  margin: 0 auto 1.25rem;
  max-width: 36rem;
  line-height: 1.5;
`;

const PinnedDateUnavailable = styled(PinnedDateNotice)`
  color: #b71c1c;
  background: rgba(183, 28, 28, 0.06);
  border-color: rgba(183, 28, 28, 0.2);
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 340px);
  gap: clamp(1rem, 3vw, 1.5rem);
  align-items: start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const calendarCardShadow = `
  0 1px 0 rgba(255, 255, 255, 0.92) inset,
  0 12px 36px rgba(16, 29, 64, 0.07)
`;

const CalendarSection = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.25rem);
  margin-bottom: 1rem;
  overflow: hidden;
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow: ${calendarCardShadow};
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-left: -0.5rem;
    margin-right: -0.5rem;
    border-radius: 14px;
  }
`;

const CalendarHeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: clamp(1rem, 2.5vw, 1.35rem);
  padding-bottom: 0.85rem;
  border-bottom: 1px solid rgba(16, 29, 64, 0.07);
`;

const CalendarNavButton = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(16, 29, 64, 0.1);
  background: #fff;
  color: ${props => props.theme.colors.accent};
  font-size: 1.35rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.95) inset,
    0 4px 14px rgba(16, 29, 64, 0.06);

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    color: #fff;
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(157, 109, 73, 0.22);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 42px;
    height: 42px;
    font-size: 1.25rem;
  }
`;

const CalendarMonthHeading = styled.h3`
  flex: 1;
  text-align: center;
  margin: 0;
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.15rem, 3vw, 1.45rem);
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const CalendarGridSurface = styled.div`
  background: linear-gradient(
    165deg,
    rgba(241, 239, 234, 0.35) 0%,
    rgba(255, 255, 255, 0.65) 100%
  );
  border: 1px solid rgba(16, 29, 64, 0.06);
  border-radius: 14px;
  padding: 0.65rem 0.5rem 0.75rem;

  @media (max-width: 480px) {
    padding: 0.5rem 0.35rem 0.65rem;
    border-radius: 12px;
  }
`;

const CalendarGridSpacer = styled.div`
  aspect-ratio: 1;
  min-width: 0;
  visibility: hidden;
  pointer-events: none;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 1rem;

  @media (max-width: 640px) {
    gap: 3px;
  }

  @media (max-width: 400px) {
    gap: 2px;
  }
`;

const WeekDay = styled.div`
  text-align: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 600;
  font-size: clamp(0.65rem, 1.8vw, 0.72rem);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.42);
  padding: 0.45rem 0.15rem;
`;

const DayLabel = styled.span`
  display: block;
  font-size: 0.5em;
  font-weight: 600;
  line-height: 1;
  margin-top: 0.15em;
  opacity: 0.95;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 4px;
  padding: 0.12em 0.18em;
  background: rgba(16, 29, 64, 0.06);

  @media (max-width: 480px) {
    font-size: 0.45em;
  }
`;

/** Flower & Garden Show dates: icon on the cell */
const FlowerGardenIconBadge = styled.span`
  display: block;
  font-size: clamp(0.52em, 2vw, 0.58em);
  line-height: 1;
  margin-top: 0.08em;
  filter: drop-shadow(0 1px 1px rgba(190, 24, 93, 0.18));

  @media (max-width: 480px) {
    font-size: 0.5em;
  }
`;

/** Pirate dates: icon only on the cell (no legend entry) */
const PirateIconBadge = styled.span`
  display: block;
  font-size: clamp(0.52em, 2vw, 0.58em);
  line-height: 1;
  margin-top: 0.08em;
  filter: drop-shadow(0 1px 1px rgba(16, 29, 64, 0.12));

  @media (max-width: 480px) {
    font-size: 0.5em;
  }
`;

const CircusDayLabel = styled.span`
  display: block;
  font-size: 0.5em;
  font-weight: 700;
  line-height: 1.05;
  margin-top: 0.12em;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #991b1b;
  background: linear-gradient(165deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid rgba(220, 38, 38, 0.38);
  border-radius: 5px;
  padding: 0.14em 0.22em;
  box-shadow: 0 1px 2px rgba(185, 28, 28, 0.08);

  @media (max-width: 480px) {
    font-size: 0.45em;
    padding: 0.12em 0.18em;
  }
`;

const EarlyCloseLabel = styled.span`
  display: block;
  font-size: 0.45em;
  font-weight: 700;
  line-height: 1;
  margin-top: 0.15em;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: linear-gradient(180deg, #ffe082 0%, #ffc107 100%);
  color: #1a3a52;
  padding: 0.2em 0.3em;
  border-radius: 5px;
  border: 1px solid rgba(224, 168, 0, 0.55);
  
  @media (max-width: 480px) {
    font-size: 0.4em;
    padding: 0.15em 0.25em;
  }
`;

const Day = styled.button`
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-style: solid;
  border-width: 1px;
  border-color: ${({ $availability, $isSelected, $flowerGardenShow }) => {
    if ($isSelected) return 'transparent';
    if ($flowerGardenShow) return 'rgba(236, 72, 153, 0.45)';
    switch (Number($availability)) {
      case 1:
        return 'rgba(16, 29, 64, 0.1)';
      case 2:
        return 'rgba(245, 158, 11, 0.45)';
      case 3:
        return 'rgba(16, 185, 129, 0.35)';
      case 4:
        return 'rgba(16, 29, 64, 0.06)';
      default:
        return 'rgba(16, 29, 64, 0.06)';
    }
  }};
  background: ${({ $availability, $isSelected, $flowerGardenShow, theme }) => {
    if ($isSelected) return theme.colors.accent;
    if ($flowerGardenShow) {
      return 'linear-gradient(165deg, #fdf2f8 0%, #fbcfe8 100%)';
    }
    switch (Number($availability)) {
      case 1:
        return '#fff';
      case 2:
        return 'linear-gradient(165deg, #fffbeb 0%, #fef3c7 100%)';
      case 3:
        return 'linear-gradient(165deg, #ecfdf5 0%, #d1fae5 100%)';
      case 4:
        return 'rgba(16, 29, 64, 0.045)';
      default:
        return 'rgba(16, 29, 64, 0.045)';
    }
  }};
  color: ${({ $isSelected, $availability }) => {
    if ($isSelected) return '#fff';
    if (Number($availability) === 4) return 'rgba(16, 29, 64, 0.32)';
    return 'rgba(16, 29, 64, 0.88)';
  }};
  border-radius: 10px;
  cursor: ${({ $availability }) => (Number($availability) === 1 ? 'pointer' : 'not-allowed')};
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.78rem, 2.4vw, 0.92rem);
  font-weight: ${({ $isSelected }) => ($isSelected ? 700 : 600)};
  min-width: 44px;
  min-height: 44px;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  box-shadow: ${({ $isSelected, $availability }) => {
    if ($isSelected) {
      return '0 2px 0 rgba(255, 255, 255, 0.25) inset, 0 8px 22px rgba(157, 109, 73, 0.35)';
    }
    if (Number($availability) === 1) {
      return '0 1px 0 rgba(255, 255, 255, 0.95) inset, 0 2px 8px rgba(16, 29, 64, 0.04)';
    }
    return 'none';
  }};

  &:hover:not(:disabled) {
    transform: ${({ $availability }) =>
      Number($availability) === 1 ? 'translateY(-1px)' : 'none'};
    ${({ $availability, $isSelected }) =>
      !$isSelected && Number($availability) === 1
        ? `
      box-shadow: 0 4px 14px rgba(16, 29, 64, 0.1);
      border-color: rgba(157, 109, 73, 0.35);
    `
        : ''}
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.accent};
    outline-offset: 2px;
  }

  &[aria-pressed='true'] ${DayLabel} {
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.35);
  }

  &[aria-pressed='true'] ${CircusDayLabel} {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.45);
    box-shadow: none;
  }

  &[aria-pressed='true'] ${PirateIconBadge} {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
  }

  &[aria-pressed='true'] ${FlowerGardenIconBadge} {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
  }

  @media (max-width: 640px) {
    min-width: 40px;
    min-height: 40px;
    border-radius: 9px;
  }

  @media (max-width: 480px) {
    min-width: 36px;
    min-height: 36px;
    border-radius: 8px;
  }
`;

const LegendPanel = styled.div`
  margin-top: 1.1rem;
  padding: 0.75rem 0.85rem;
  background: linear-gradient(
    180deg,
    rgba(241, 239, 234, 0.5) 0%,
    rgba(255, 255, 255, 0.92) 100%
  );
  border: 1px solid rgba(16, 29, 64, 0.07);
  border-radius: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem 0.75rem;
  justify-content: center;
  align-items: center;

  @media (max-width: 480px) {
    padding: 0.65rem 0.6rem;
    gap: 0.4rem 0.55rem;
  }
`;

const LegendSwatch = styled.span`
  width: 0.95rem;
  height: 0.95rem;
  border-radius: 5px;
  flex-shrink: 0;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.9) inset;
  background: ${({ $bg }) => $bg};
  border: ${({ $border }) => $border || 'none'};
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.72rem, 1.8vw, 0.8rem);
  color: rgba(16, 29, 64, 0.58);
  line-height: 1.25;
`;

const LegendMiniBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 0.22em 0.38em;
  border-radius: 5px;
  line-height: 1;
  letter-spacing: 0.02em;
`;

const SolsticeContinuePanel = styled.div`
  position: sticky;
  top: 168px;
  height: fit-content;
  background: #fff;
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.5rem);
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow: ${calendarCardShadow};
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1rem;

  @media (max-width: 992px) {
    position: static;
    margin-top: 1rem;
  }
`;

const SolsticeContinueTitle = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin: 0;
  text-align: center;
`;

const SolsticeContinueSubtitle = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: rgba(16, 29, 64, 0.6);
  margin: 0;
  text-align: center;
  line-height: 1.5;
`;

const SolsticeConfirmedBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(157, 109, 73, 0.08);
  border: 1px solid rgba(157, 109, 73, 0.3);
  border-radius: 10px;
  padding: 0.65rem 1rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #9d6d49;
`;

const SolsticeContinueButton = styled.button`
  width: 100%;
  padding: 0.9rem 1.5rem;
  background: ${props => props.disabled
    ? 'rgba(16, 29, 64, 0.12)'
    : 'linear-gradient(135deg, #9d6d49 0%, #7a5238 100%)'};
  color: ${props => props.disabled ? 'rgba(16, 29, 64, 0.4)' : '#fff'};
  border: none;
  border-radius: 12px;
  font-family: ${props => props.theme.fonts.main};
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-shadow: ${props => props.disabled
    ? 'none'
    : '0 4px 14px rgba(157, 109, 73, 0.35)'};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(157, 109, 73, 0.45);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const TimeSlotsSection = styled.div`
  position: sticky;
  top: 168px;
  height: fit-content;
  background: #fff;
  border-radius: 16px;
  padding: clamp(1rem, 2.5vw, 1.25rem);
  border: 1px solid rgba(16, 29, 64, 0.08);
  box-shadow: ${calendarCardShadow};
  
  @media (max-width: 992px) {
    position: static;
    margin-top: 1rem;
    padding: 1.25rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.15rem;
  }
`;

const TimeSlotsTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.15rem);
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  font-weight: 700;
`;

const FlexibleArrivalHint = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: clamp(0.88rem, 1.75vw, 0.94rem);
  line-height: 1.55;
  color: rgba(16, 29, 64, 0.62);
  margin: 0 0 1rem;
`;

const TimeSlotsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 0.75rem;
  margin-top: 0.85rem;
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
  }
`;

const TimeSlot = styled.button`
  padding: 1rem;
  border: 1px solid
    ${props =>
      props.$isSelected ? 'rgba(157, 109, 73, 0.45)' : 'rgba(16, 29, 64, 0.1)'};
  background: ${props => props.$isSelected ? props.theme.colors.accent : 'white'};
  color: ${props => props.$isSelected ? 'white' : props.theme.colors.primary};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: 600;
  min-height: 48px;
  box-shadow: ${props =>
    props.$isSelected
      ? '0 8px 24px rgba(157, 109, 73, 0.25)'
      : '0 1px 0 rgba(255, 255, 255, 0.95) inset'};
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.colors.accent};
    background: ${props => props.$isSelected ? props.theme.colors.accent : props.theme.colors.offWhite};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.95rem;
  }
`;

const SelectedDateInfo = styled.div`
  background: linear-gradient(
    180deg,
    rgba(241, 239, 234, 0.85) 0%,
    rgba(255, 255, 255, 0.98) 100%
  );
  color: ${props => props.theme.colors.primary};
  padding: 1.15rem 1.25rem;
  border-radius: 14px;
  margin-bottom: 1.25rem;
  text-align: center;
  border: 1px solid rgba(157, 109, 73, 0.28);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.92) inset,
    0 10px 28px rgba(16, 29, 64, 0.06);
  
  @media (max-width: 768px) {
    padding: 1rem 1.1rem;
    margin-bottom: 1rem;
  }
`;

const SellingFast = styled.div`
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #b94040;
  margin-top: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  &::before {
    content: '●';
    font-size: 0.45rem;
    animation: sellingFastPulse 1.4s ease-in-out infinite;
  }
  @keyframes sellingFastPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
`;

const SelectedDateText = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const SelectedTimeText = styled.div`
  font-size: 1rem;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const InfoBox = styled.div`
  background: linear-gradient(
    165deg,
    rgba(241, 239, 234, 0.9) 0%,
    rgba(255, 255, 255, 0.95) 100%
  );
  border: 1px solid rgba(157, 109, 73, 0.28);
  border-radius: 14px;
  padding: 1rem 1.15rem;
  margin-top: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 0.95rem 1rem;
    margin-top: 1.25rem;
  }
`;

const InfoText = styled.p`
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: rgba(16, 29, 64, 0.62);
  margin: 0;
  font-size: clamp(0.88rem, 1.75vw, 0.94rem);
  line-height: 1.5;
`;

const FLOWER_GARDEN_PAGE_ID = '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09';
const AFTERNOON_TEA_PAGE_ID = '8b64eaf5-431e-4a3e-9194-e243e5cbdbdc';
const PIRATE_CIRCUS_PAGE_ID = '27d7f7cb-fd23-4c0c-bd71-8420c022ccf5';
/** Flexible visit: pick an arrival slot but guests may come and go all day */
const FLEXIBLE_ARRIVAL_DAY_PAGE_ID = 'e170beff-cd9f-4f2a-ba2c-ac39284cfb7f';
/** Summer Solstice — time is fixed per product; hide the picker and auto-select silently */
const SUMMER_SOLSTICE_PAGE_ID = '0efbadd8-388c-4ad0-ba36-c88282ac634d';

const normalizeTimeSlot = (slot) => {
  if (typeof slot === 'string') return { time: slot };
  return slot;
};

const getSlotTime = (slot) => {
  if (!slot) return '';
  if (typeof slot === 'string') return slot;
  return slot.time || '';
};

// Animal Park / Ultimate Easter pages — Easter label on calendar dates in-range
const EARLY_BIRD_PAGE_IDS = [
  'b0f1a62a-a34a-4c4e-a88d-6fda03a19279',
  '03c1719d-b7ff-43b1-acd9-72c6fdd56f6a'
];

// Additional pages that show Easter label only (no early bird pricing/banner)
const EASTER_LABEL_PAGE_IDS = [
  '40ca044f-534f-4b7f-83ea-b4156f0633d3',
  'd97b9888-0f4d-4305-a87b-b9eaf6818a6c'
];

// Easter half term range: 30 March - 19 April. Shown on early bird pages.
const getEasterHalfTermRange = () => {
  const year = new Date().getFullYear();
  return {
    start: new Date(year, 2, 30),   // 30 March
    end: new Date(year, 3, 19)      // 19 April
  };
};

const isEasterHalfTermDate = (date, pageId) => {
  if (!pageId || !EARLY_BIRD_PAGE_IDS.includes(pageId)) return false;
  const { start, end } = getEasterHalfTermRange();
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const rangeStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const rangeEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return d >= rangeStart && d <= rangeEnd;
};

// Check if date should show Easter label for additional pages (without early bird features)
const isEasterLabelDate = (date, pageId) => {
  if (!pageId || !EASTER_LABEL_PAGE_IDS.includes(pageId)) return false;
  const { start, end } = getEasterHalfTermRange();
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const rangeStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const rangeEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return d >= rangeStart && d <= rangeEnd;
};

// Circus: 23–25 May. Shown on calendar for all booking journey pages.
const getCircusDateRange = () => {
  const year = new Date().getFullYear();
  return {
    start: new Date(year, 4, 23),   // 23 May (month 4 = May)
    end: new Date(year, 4, 25)      // 25 May
  };
};

const isCircusDate = (date) => {
  const { start, end } = getCircusDateRange();
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const rangeStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const rangeEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return d >= rangeStart && d <= rangeEnd;
};

// Pirate Takeover: 23–31 May. Shown on calendar for all booking journey pages.
const getPirateTakeoverRange = () => {
  const year = new Date().getFullYear();
  return {
    start: new Date(year, 4, 23),   // 23 May
    end: new Date(year, 4, 31)     // 31 May
  };
};

const isPirateTakeoverDate = (date) => {
  const { start, end } = getPirateTakeoverRange();
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const rangeStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const rangeEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return d >= rangeStart && d <= rangeEnd;
};

// Castle closes early at 3pm on 20th June 2026
const isEarlyCloseDate = (date) => {
  return date.getFullYear() === 2026 && date.getMonth() === 5 && date.getDate() === 20;
};

// Flower & Garden Show: 11–12 July. Shown on calendar for all booking journey pages.
const getFlowerGardenShowRange = () => {
  const year = new Date().getFullYear();
  return {
    start: new Date(year, 6, 11),   // 11 July
    end: new Date(year, 6, 12)      // 12 July
  };
};

const isFlowerGardenShowDate = (date) => {
  const { start, end } = getFlowerGardenShowRange();
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const rangeStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const rangeEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return d >= rangeStart && d <= rangeEnd;
};

const formatYmdKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DateSelection = ({ 
  availableDates,
  pinnedVisitDate,
  filterMonth,
  selectedDate, 
  timeSlots, 
  selectedTimeSlot, 
  onDateSelect, 
  onTimeSlotSelect,
  pageId,
  onContinue,
  canContinue,
}) => {
  const pinnedDate = useMemo(
    () => (pinnedVisitDate ? visitDateFromYmdKey(pinnedVisitDate) : null),
    [pinnedVisitDate]
  );
  const isDatePinned = Boolean(pinnedVisitDate);
  const isSolsticePage = pageId === SUMMER_SOLSTICE_PAGE_ID;
  const onTimeSlotSelectRef = useRef(onTimeSlotSelect);
  const autoSelectKeyRef = useRef('');

  onTimeSlotSelectRef.current = onTimeSlotSelect;

  // Parse filterMonth (YYYY-MM) into a Date so we can jump to it and restrict days
  const filterMonthDate = useMemo(() => {
    if (!filterMonth) return null;
    const [y, m] = filterMonth.split('-').map(Number);
    if (!y || !m) return null;
    return new Date(y, m - 1, 1);
  }, [filterMonth]);

  const [currentMonth, setCurrentMonth] = useState(() =>
    filterMonthDate || pinnedDate || new Date()
  );

  // Function to find the next available date
  const findNextAvailableDate = () => {
    if (pinnedVisitDate && availableDates[pinnedVisitDate] === 1 && pinnedDate) {
      return pinnedDate;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    // Convert availableDates object to array of dates and sort them
    const availableDateStrings = Object.keys(availableDates || {})
      .filter(dateString => {
        const date = new Date(dateString);
        date.setHours(0, 0, 0, 0);
        const availability = availableDates[dateString];
        // Only include future dates that are available (availability === 1)
        if (date < today || availability !== 1) {
          return false;
        }
        
        // For afternoon tea page, apply front-end restrictions
        if (pageId === AFTERNOON_TEA_PAGE_ID) {
          const currentYear = new Date().getFullYear();
          const march14 = new Date(currentYear, 2, 14); // Month is 0-indexed, so 2 = March
          march14.setHours(0, 0, 0, 0);
          
          // Exclude dates before March 14th
          if (date < march14) {
            return false;
          }
          
          // Exclude the 14th, 15th, and 16th (closed dates, opens on 19th)
          const dayOfMonth = date.getDate();
          if (dayOfMonth === 14 || dayOfMonth === 15 || dayOfMonth === 16) {
            return false;
          }
          
          // Exclude Tuesdays and Wednesdays
          const dayOfWeek = (date.getDay() + 6) % 7; // Monday = 0, Tuesday = 1, Wednesday = 2, etc.
          if (dayOfWeek === 1 || dayOfWeek === 2) {
            return false;
          }
        }
        
        return true;
      })
      .sort(); // Sort chronologically
    
    if (availableDateStrings.length > 0) {
      return new Date(availableDateStrings[0]);
    }
    
    // If no available dates found, for afternoon tea page, start from March 14th
    if (pageId === AFTERNOON_TEA_PAGE_ID) {
      const currentYear = new Date().getFullYear();
      const march14 = new Date(currentYear, 2, 14); // Month is 0-indexed, so 2 = March
      march14.setHours(0, 0, 0, 0);
      
      // If March 14th is in the past, use it anyway (will show next month)
      if (march14 < today) {
        return march14;
      }
      
      // Find the next available day after March 14th that's not Tuesday or Wednesday
      let checkDate = new Date(march14);
      for (let i = 0; i < 7; i++) {
        const dayOfWeek = (checkDate.getDay() + 6) % 7;
        if (dayOfWeek !== 1 && dayOfWeek !== 2) {
          return checkDate;
        }
        checkDate.setDate(checkDate.getDate() + 1);
      }
      
      return march14;
    }
    
    // If no available dates found, return current month
    return new Date();
  };

  // Reset auto-select guard when the visit date changes
  useEffect(() => {
    autoSelectKeyRef.current = '';
  }, [selectedDate]);

  // Auto-select silently: solstice page only (times are fixed per product), or when one slot exists
  useEffect(() => {
    if (!timeSlots.length || selectedTimeSlot) return;
    if (!isSolsticePage && timeSlots.length !== 1) return;

    const autoSelectKey = timeSlots.map(getSlotTime).join('|');
    if (autoSelectKeyRef.current === autoSelectKey) return;
    autoSelectKeyRef.current = autoSelectKey;

    onTimeSlotSelectRef.current(normalizeTimeSlot(timeSlots[0]));
  }, [timeSlots, selectedTimeSlot, isSolsticePage]);

  // Update currentMonth when availableDates changes to show the month with next available date
  useEffect(() => {
    if (pinnedDate) {
      setCurrentMonth(pinnedDate);
      return;
    }
    if (availableDates && Object.keys(availableDates).length > 0) {
      const nextAvailableDate = findNextAvailableDate();

      setCurrentMonth(nextAvailableDate);
    }
  }, [availableDates, pinnedDate, pinnedVisitDate]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    // Adjust firstDay so Monday is 0, Tuesday is 1, ..., Sunday is 6
    const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
    return { days, firstDay };
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  const getDateAvailability = (date) => {
    const dateString = formatYmdKey(date);

    if (pinnedVisitDate && dateString !== pinnedVisitDate) {
      return 4;
    }

    // filterMonth: only allow dates in the specified month (e.g. June 2026)
    if (filterMonthDate) {
      if (
        date.getFullYear() !== filterMonthDate.getFullYear() ||
        date.getMonth() !== filterMonthDate.getMonth()
      ) {
        return 4;
      }
    }

    const baseAvailability = availableDates[dateString] || 4; // Default to not available
    
    // For afternoon tea page, apply special restrictions
    if (pageId === AFTERNOON_TEA_PAGE_ID) {
      const currentYear = new Date().getFullYear();
      const march14 = new Date(currentYear, 2, 14); // Month is 0-indexed, so 2 = March
      march14.setHours(0, 0, 0, 0);
      
      // Disable all dates before March 14th
      if (date < march14) {
        return 4; // Not available
      }
      
      // Disable the 14th, 15th, and 16th (closed dates, opens on 19th)
      const dayOfMonth = date.getDate();
      if (dayOfMonth === 14 || dayOfMonth === 15 || dayOfMonth === 16) {
        return 4; // Not available
      }
      
      // Get day of week: Monday = 0, Tuesday = 1, Wednesday = 2, ..., Sunday = 6
      const dayOfWeek = (date.getDay() + 6) % 7;
      // Tuesday (1) and Wednesday (2) are not available for afternoon tea
      if (dayOfWeek === 1 || dayOfWeek === 2) {
        return 4; // Not available
      }
    }
    
    return baseAvailability;
  };

  const renderCalendar = () => {
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const { days, firstDay } = getDaysInMonth(currentMonth);
    const today = new Date();
    // Normalize today to start of day for proper comparison
    today.setHours(0, 0, 0, 0);

    return (
      <CalendarSection>
        <CalendarHeaderBar>
          {!isDatePinned && !filterMonthDate && (
            <CalendarNavButton
              type="button"
              onClick={handlePrevMonth}
              aria-label="Previous month"
            >
              ‹
            </CalendarNavButton>
          )}
          {(isDatePinned || filterMonthDate) && <span style={{ width: 44 }} aria-hidden />}
          <CalendarMonthHeading>
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </CalendarMonthHeading>
          {!isDatePinned && !filterMonthDate && (
            <CalendarNavButton
              type="button"
              onClick={handleNextMonth}
              aria-label="Next month"
            >
              ›
            </CalendarNavButton>
          )}
          {(isDatePinned || filterMonthDate) && <span style={{ width: 44 }} aria-hidden />}
        </CalendarHeaderBar>
        <CalendarGridSurface>
        <CalendarGrid>
          {weekDays.map(day => (
            <WeekDay key={day}>{day}</WeekDay>
          ))}
          {[...Array(firstDay)].map((_, index) => (
            <CalendarGridSpacer key={`empty-${index}`} aria-hidden />
          ))}
          {[...Array(days)].map((_, index) => {
            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), index + 1);
            // Normalize date to start of day for proper comparison
            date.setHours(0, 0, 0, 0);
            const availability = getDateAvailability(date);
            const isSelected = selectedDate &&
              date.getDate() === selectedDate.getDate() &&
              date.getMonth() === selectedDate.getMonth() &&
              date.getFullYear() === selectedDate.getFullYear();
            // Check if date is in the past (before today, not including today)
            const isPast = date < today;
            
            const easterHalfTerm = isEasterHalfTermDate(date, pageId);
            const easterLabel = isEasterLabelDate(date, pageId);
            const circusDate = isCircusDate(date);
            const pirateTakeover = isPirateTakeoverDate(date);
            const flowerGardenShow = isFlowerGardenShowDate(date);
            const earlyClose = isEarlyCloseDate(date);
            
            // Check restrictions for afternoon tea page
            let isAfternoonTeaRestricted = false;
            let tooltipText = null;
            
            if (!tooltipText && (circusDate || pirateTakeover || flowerGardenShow)) {
              const parts = [];
              if (circusDate) parts.push('Circus (23–25 May)');
              if (pirateTakeover) parts.push('🏴‍☠️ Pirate Takeover (23–31 May)');
              if (flowerGardenShow) parts.push('🌸 Flower & Garden Show (11–12 July)');
              tooltipText = parts.join(' · ');
            }
            if (earlyClose) {
              tooltipText = '⚠️ Castle closing early at 3pm on this date';
            }
            
            if (pageId === AFTERNOON_TEA_PAGE_ID) {
              const currentYear = new Date().getFullYear();
              const march14 = new Date(currentYear, 2, 14); // Month is 0-indexed, so 2 = March
              march14.setHours(0, 0, 0, 0);
              
              // Check if date is before March 14th
              if (date < march14) {
                isAfternoonTeaRestricted = true;
                tooltipText = 'Afternoon Tea is not available before March 14th';
              } else {
                // Check if this is the 14th, 15th, or 16th (closed dates, opens on 19th)
                const dayOfMonth = date.getDate();
                if (dayOfMonth === 14 || dayOfMonth === 15 || dayOfMonth === 16) {
                  isAfternoonTeaRestricted = true;
                  tooltipText = 'Not available on this date. Opens on the 19th.';
                } else {
                  // Check if this is a Tuesday or Wednesday
                  const dayOfWeek = (date.getDay() + 6) % 7; // Monday = 0, Tuesday = 1, Wednesday = 2, etc.
                  if (dayOfWeek === 1 || dayOfWeek === 2) {
                    isAfternoonTeaRestricted = true;
                    tooltipText = 'Afternoon Tea is not available on this date';
                  }
                }
              }
            }

            return (
              <Day
                key={index}
                type="button"
                aria-pressed={isSelected}
                aria-label={
                  tooltipText
                    ? `${index + 1} ${currentMonth.toLocaleString('default', { month: 'long' })}: ${tooltipText}`
                    : `${index + 1} ${currentMonth.toLocaleString('default', { month: 'long' })}`
                }
                $availability={isPast ? 4 : availability}
                $isSelected={isSelected}
                $flowerGardenShow={flowerGardenShow && !isSelected}
                onClick={() => !isPast && availability === 1 && !isAfternoonTeaRestricted && onDateSelect(date)}
                disabled={isPast || availability !== 1 || isAfternoonTeaRestricted}
                title={tooltipText || (easterHalfTerm || easterLabel ? 'Easter' : undefined)}
              >
                {index + 1}
                {(easterHalfTerm || easterLabel) && <DayLabel>Easter</DayLabel>}
                {circusDate && <CircusDayLabel>Circus</CircusDayLabel>}
                {flowerGardenShow && (
                  <FlowerGardenIconBadge aria-hidden title="Flower & Garden Show">
                    🌸
                  </FlowerGardenIconBadge>
                )}
                {pirateTakeover && (
                  <PirateIconBadge aria-hidden title="Pirate Takeover">
                    🏴‍☠️
                  </PirateIconBadge>
                )}
                {earlyClose && !isSolsticePage && <EarlyCloseLabel>Closes 3pm</EarlyCloseLabel>}
              </Day>
            );
          })}
        </CalendarGrid>
        </CalendarGridSurface>
        <LegendPanel>
          <LegendItem>
            <LegendSwatch $bg="rgba(16, 29, 64, 0.06)" />
            Not available
          </LegendItem>
          <LegendItem>
            <LegendSwatch
              $bg="linear-gradient(165deg, #fffbeb 0%, #fef3c7 100%)"
              $border="1px solid rgba(245, 158, 11, 0.45)"
            />
            Limited availability
          </LegendItem>
          <LegendItem>
            <LegendSwatch
              $bg="linear-gradient(165deg, #ecfdf5 0%, #d1fae5 100%)"
              $border="1px solid rgba(16, 185, 129, 0.35)"
            />
            Booking duration
          </LegendItem>
          <LegendItem>
            <LegendSwatch
              $bg="#fff"
              $border="1px solid rgba(16, 29, 64, 0.1)"
            />
            Available
          </LegendItem>
          <LegendItem>
            <LegendMiniBadge
              style={{
                background: 'linear-gradient(165deg, #fef2f2 0%, #fee2e2 100%)',
                color: '#991b1b',
                border: '1px solid rgba(220, 38, 38, 0.38)',
                fontWeight: 700,
              }}
            >
              Circus
            </LegendMiniBadge>
            Circus 23–25 May
          </LegendItem>
          <LegendItem>
            <LegendSwatch
              $bg="linear-gradient(165deg, #fdf2f8 0%, #fbcfe8 100%)"
              $border="1px solid rgba(236, 72, 153, 0.45)"
            />
            🌸 Flower & Garden Show 11–12 Jul
          </LegendItem>
          {!isSolsticePage && (
            <LegendItem>
              <LegendMiniBadge
                style={{
                  background: 'linear-gradient(180deg, #ffe082 0%, #ffc107 100%)',
                  color: '#1a3a52',
                  border: '1px solid rgba(224, 168, 0, 0.55)',
                }}
              >
                3pm
              </LegendMiniBadge>
              Castle closes 3pm (20 Jun)
            </LegendItem>
          )}
        </LegendPanel>
      </CalendarSection>
    );
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => getSlotTime(timeSlot);

  const isTimeSlotSelected = (timeSlot) => {
    if (!selectedTimeSlot) return false;

    const time = getSlotTime(timeSlot);
    const selectedTime = getSlotTime(selectedTimeSlot);
    if (time && selectedTime) {
      return time === selectedTime;
    }

    const slotObj = normalizeTimeSlot(timeSlot);
    const selectedObj = normalizeTimeSlot(selectedTimeSlot);
    if (slotObj.eventId && selectedObj.eventId) {
      return slotObj.eventId === selectedObj.eventId;
    }

    return false;
  };

  const pinnedDateLabel = pinnedDate
    ? pinnedDate.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;
  const pinnedDateUnavailable =
    isDatePinned &&
    Object.keys(availableDates || {}).length > 0 &&
    availableDates[pinnedVisitDate] !== 1;

  return (
    <DateContainer>
      <PageHeader>
        <SectionEyebrow>Your visit</SectionEyebrow>
        <SectionTitle>
          {isDatePinned ? 'Confirm your visit date' : 'Select Your Visit Date'}
        </SectionTitle>
        <SectionSubtitle>
          {isSolsticePage
            ? 'Choose your date from the calendar — your time is confirmed automatically.'
            : isDatePinned
            ? 'Your booking is for the date below. Select it on the calendar, then choose a time slot if required.'
            : 'Choose an available date from the calendar. Bookable dates appear in white; then pick a time slot when one is required.'}
        </SectionSubtitle>
        {isDatePinned && pinnedDateLabel && !pinnedDateUnavailable && (
          <PinnedDateNotice>
            Booking for {pinnedDateLabel}
          </PinnedDateNotice>
        )}
        {pinnedDateUnavailable && (
          <PinnedDateUnavailable>
            {pinnedDateLabel} is not currently available for the tickets you selected.
            Please try fewer tickets or choose a different visit.
          </PinnedDateUnavailable>
        )}
      </PageHeader>

      <Layout>
        <div>
          {renderCalendar()}

          {selectedDate && (
            <SelectedDateInfo>
              <SelectedDateText>
                Selected Date: {formatDate(selectedDate)}
              </SelectedDateText>
              {selectedTimeSlot && !isSolsticePage && (
                <SelectedTimeText>
                  Selected Time: {formatTime(selectedTimeSlot)}
                </SelectedTimeText>
              )}
              {selectedDate && !selectedTimeSlot && timeSlots.length > 0 && !isSolsticePage && (
                <SelectedTimeText>
                  Please select a time slot below
                </SelectedTimeText>
              )}
            </SelectedDateInfo>
          )}

          <InfoBox>
            <InfoText>
              Select your preferred visit date from the calendar above. Available dates are shown in white.
              {timeSlots.length > 0 && !isSolsticePage && ' Then choose a time slot from the right panel.'}
              {pageId === AFTERNOON_TEA_PAGE_ID && (
                <div style={{ marginTop: '0.75rem', fontWeight: 600, color: '#d32f2f' }}>
                  Note: Afternoon Tea is not available before March 14th, and is not available on Tuesdays and Wednesdays.
                </div>
              )}
            </InfoText>
          </InfoBox>
        </div>

        {isSolsticePage && (
          <SolsticeContinuePanel>
            {selectedDate ? (
              <>
                <SolsticeContinueTitle>Your Date is Set</SolsticeContinueTitle>
                <SolsticeConfirmedBadge>
                  ✓ Date confirmed — time assigned automatically
                </SolsticeConfirmedBadge>
                <SolsticeContinueSubtitle>
                  Your event time will be confirmed in your booking summary.
                </SolsticeContinueSubtitle>
                <SolsticeContinueButton
                  disabled={!canContinue}
                  onClick={onContinue}
                >
                  Continue →
                </SolsticeContinueButton>
              </>
            ) : (
              <>
                <SolsticeContinueTitle>Select a Date</SolsticeContinueTitle>
                <SolsticeContinueSubtitle>
                  Choose your preferred date from the calendar on the left to continue.
                </SolsticeContinueSubtitle>
                <SolsticeContinueButton disabled>
                  Continue →
                </SolsticeContinueButton>
              </>
            )}
          </SolsticeContinuePanel>
        )}

        {selectedDate && !isSolsticePage && (
          <TimeSlotsSection>
            <TimeSlotsTitle>
              {pageId === FLEXIBLE_ARRIVAL_DAY_PAGE_ID
                ? 'Choose your arrival time'
                : 'Available Time Slots'}
            </TimeSlotsTitle>
            {pageId === FLEXIBLE_ARRIVAL_DAY_PAGE_ID && (
              <FlexibleArrivalHint>
                Select any arrival time. Come and go as much as you like throughout the day — drop in and out as much as you like.
              </FlexibleArrivalHint>
            )}
            {pageId === FLOWER_GARDEN_PAGE_ID ? (
              <div style={{ lineHeight: 1.5 }}>
                This is an all-day event. A time will be applied automatically.
                {selectedTimeSlot?.time && (
                  <div style={{ marginTop: '0.5rem', fontWeight: 600 }}>
                    Selected Time: {formatTime(selectedTimeSlot)}
                  </div>
                )}
              </div>
            ) : timeSlots.length > 0 ? (
              <TimeSlotsGrid>
                {timeSlots.map((timeSlot, index) => {
                  const isSelected = isTimeSlotSelected(timeSlot);
                  const timeStr = formatTime(timeSlot);
                  const is11am = timeStr.startsWith('11:');
                  const showSellingFast = pageId === PIRATE_CIRCUS_PAGE_ID && is11am;
                  return (
                    <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                      <TimeSlot
                        type="button"
                        $isSelected={isSelected}
                        onClick={() => onTimeSlotSelect(normalizeTimeSlot(timeSlot))}
                        style={{ width: '100%' }}
                      >
                        {isSelected && '✓ '}
                        {timeStr}
                      </TimeSlot>
                      {showSellingFast && <SellingFast>Selling fast</SellingFast>}
                    </div>
                  );
                })}
              </TimeSlotsGrid>
            ) : (
              <div>No specific timeslots required for this date.</div>
            )}
          </TimeSlotsSection>
        )}
      </Layout>
    </DateContainer>
  );
};

export default DateSelection;